import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";
import {
  Bell,
  BookText,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  Download,
  FileArchive,
  FileCheck,
  FileText,
  Folder,
  GraduationCap,
  LayoutDashboard,
  Menu,
  Play,
  Search,
  Users,
  Video,
} from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import "./marketing.css";

const FILE_SCREENS = ["markschemes", "extra", "pdfs", "pastpapers"];

const FEATURES = [
  {
    id: "home",
    label: "Dashboard",
    title: "A home screen that shows what matters today",
    copy: "Students land on a welcome view for their course: syllabus progress, the latest announcement, upcoming homework, and the chapter they just finished.",
  },
  {
    id: "progress",
    label: "Progress",
    title: "Chapter progress, marked as it is taught",
    copy: "Each chapter shows as complete or still ahead, with an overall percentage so students can see how far through the syllabus they are.",
  },
  {
    id: "videos",
    label: "Videos",
    title: "Lesson videos grouped by chapter",
    copy: "Recorded lessons sit under the chapter they belong to. Students can search, open a topic, and play the video inside the dashboard.",
  },
  {
    id: "markschemes",
    label: "Mark schemes",
    title: "Mark schemes ready to download",
    copy: "Official-style mark schemes are filed by chapter, so students can check how marks are awarded after they attempt a question.",
  },
  {
    id: "extra",
    label: "Extra questions",
    title: "Extra practice beyond the lesson",
    copy: "Additional question sets are organised by chapter and open as downloadable files when a student wants more practice.",
  },
  {
    id: "pdfs",
    label: "PDFs",
    title: "Notes and handouts in one place",
    copy: "Course PDFs — notes, summaries, and handouts — are listed by chapter with a direct download for each file.",
  },
  {
    id: "pastpapers",
    label: "Past papers",
    title: "Past papers by year and session",
    copy: "Exam papers are grouped by year and session. Completed papers are ticked so students can track what they have already attempted.",
  },
  {
    id: "homework",
    label: "Homework",
    title: "Assignments with due dates and status",
    copy: "Every homework shows its due date and whether it is done, still incomplete, or missing, so nothing sits unnoticed.",
  },
  {
    id: "exams",
    label: "Exam grades",
    title: "Exam grades, marks, and comments",
    copy: "After each paper, students see the name, date, score, grade, and any written comment from the teacher.",
  },
  {
    id: "meetings",
    label: "Online meetings",
    title: "Live sessions with a join link",
    copy: "Upcoming and past meetings are listed with the time and a short description. When a session is live, students can join from the card.",
  },
  {
    id: "announcements",
    label: "Announcements",
    title: "Course news in date order",
    copy: "Class updates — timetable changes, reminders, and exam notes — appear as dated cards for the student’s enrolled course.",
  },
  {
    id: "profile",
    label: "Profile",
    title: "A profile with identity and course status",
    copy: "The top bar opens a profile with the student’s name, email, student ID, current level, and an active enrolment status.",
  },
];

const NAV = [
  { id: "home", label: "Dashboard", icon: LayoutDashboard },
  { id: "progress", label: "Progress", icon: BookText },
  { id: "videos", label: "Videos", icon: Video },
  {
    id: "files",
    label: "Files",
    icon: Folder,
    children: [
      { id: "markschemes", label: "Mark Schemes", icon: FileCheck },
      { id: "extra", label: "Extra Questions", icon: FileText },
      { id: "pdfs", label: "PDFs", icon: FileText },
      { id: "pastpapers", label: "Past Papers", icon: FileArchive },
    ],
  },
  { id: "homework", label: "Homework", icon: CalendarDays },
  { id: "exams", label: "Exams Grades", icon: GraduationCap },
  { id: "meetings", label: "Online Meetings", icon: Users },
  { id: "announcements", label: "Announcements", icon: Bell },
];

function DashFrame({ active, path = "dashboard", children }) {
  const filesOpen = FILE_SCREENS.includes(active);

  return (
    <div className="xm-window" aria-hidden="true">
      <div className="xm-window-bar">
        <div className="xm-window-dots">
          <span />
          <span />
          <span />
        </div>
        <div className="xm-window-url">X-Maths.com/{path}</div>
      </div>
      <div className="xm-dash">
        <aside className="xm-dash-side">
          <div className="xm-dash-brand">
            <strong>X-MATHS</strong>
            <span>Course: IGCSE OL Mathematics</span>
          </div>
          <ul className="xm-dash-nav">
            {NAV.map((item) => {
              const Icon = item.icon;
              if (item.children) {
                return (
                  <li key={item.id}>
                    <div className={`xm-dash-link ${filesOpen ? "is-active" : ""}`}>
                      <Icon size={12} />
                      <span>{item.label}</span>
                      <ChevronRight size={11} style={{ marginLeft: "auto", transform: filesOpen ? "rotate(90deg)" : undefined }} />
                    </div>
                    {filesOpen && (
                      <ul className="xm-dash-sub">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          return (
                            <li key={child.id} className={`xm-dash-link ${active === child.id ? "is-active" : ""}`}>
                              <ChildIcon size={11} />
                              <span>{child.label}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              }
              return (
                <li key={item.id} className={`xm-dash-link ${active === item.id ? "is-active" : ""}`}>
                  <Icon size={12} />
                  <span>{item.label}</span>
                </li>
              );
            })}
          </ul>
        </aside>
        <div className="xm-dash-main">
          <div className="xm-dash-top">
            <div className={`xm-dash-user ${active === "profile" ? "is-active" : ""}`}>
              <div className="xm-avatar">LH</div>
              <div>
                <strong>Lina</strong>
                <small>XM-2041</small>
              </div>
            </div>
          </div>
          <div className="xm-dash-mobile-top">
            <div className="xm-dash-mobile-brand">
              <Menu size={14} />
              <strong>X-MATHS</strong>
            </div>
            <div className={`xm-dash-user ${active === "profile" ? "is-active" : ""}`}>
              <div className="xm-avatar">LH</div>
              <div>
                <strong>Lina</strong>
                <small>XM-2041</small>
              </div>
            </div>
          </div>
          <div className="xm-dash-body">{children}</div>
        </div>
      </div>
    </div>
  );
}

function Screen({ id }) {
  if (id === "home") {
    return (
      <DashFrame active="home">
        <div className="xm-hero-card">
          <h3>Welcome back, Lina Hassan!</h3>
          <p>Course: IGCSE OL Mathematics</p>
        </div>
        <div className="xm-split">
          <div className="xm-panel">
            <h4>Syllabus Progress</h4>
            <div className="xm-kv" style={{ background: "transparent", padding: 0 }}>
              <span>Overall Progress</span>
              <strong>68%</strong>
            </div>
            <div className="xm-bar" style={{ marginTop: 6 }}><span /></div>
            <div className="xm-mini-stats">
              <div><span className="xm-muted">Completed</span><strong>11</strong></div>
              <div><span className="xm-muted">Total</span><strong>16</strong></div>
            </div>
          </div>
          <div className="xm-panel">
            <h4>Latest Announcement</h4>
            <h3>Mock week timetable</h3>
            <p className="xm-muted">Paper 2 mock is on Thursday. Bring a calculator and tracing paper.</p>
          </div>
        </div>
        <div className="xm-split">
          <div className="xm-panel">
            <h4>Upcoming Assignments</h4>
            <div className="xm-row" style={{ padding: "0.35rem 0", border: 0, background: "transparent" }}>
              <div className="xm-row-copy">
                <h3>Algebra practice set</h3>
                <p>Due: Oct 3</p>
              </div>
            </div>
          </div>
          <div className="xm-panel">
            <h4>Latest Chapter Completed</h4>
            <h3>Quadratic equations</h3>
            <p className="xm-muted">Completed on Sep 26</p>
          </div>
        </div>
      </DashFrame>
    );
  }

  if (id === "progress") {
    const chapters = [
      ["Number", true],
      ["Algebra", true],
      ["Quadratic equations", true],
      ["Graphs", false],
      ["Geometry", false],
    ];
    return (
      <DashFrame active="progress">
        <div className="xm-hero-card" style={{ display: "flex", justifyContent: "space-between" }}>
          <h3>Course Chapters</h3>
          <p style={{ margin: 0 }}>Progress: 68%</p>
        </div>
        <div className="xm-panel">
          <div className="xm-kv" style={{ background: "transparent", padding: 0 }}>
            <span>Overall Progress</span>
            <strong>68%</strong>
          </div>
          <div className="xm-bar" style={{ marginTop: 6 }}><span /></div>
        </div>
        {chapters.map(([name, done]) => (
          <div className="xm-row" key={name}>
            <div className="xm-ico"><BookText size={12} /></div>
            <div className="xm-row-copy"><h3>{name}</h3></div>
            {done ? <CheckCircle2 size={14} color="#22c55e" /> : <Circle size={14} color="#6b7280" />}
          </div>
        ))}
      </DashFrame>
    );
  }

  if (id === "videos") {
    return (
      <DashFrame active="videos">
        <div className="xm-hero-card" style={{ display: "flex", justifyContent: "space-between" }}>
          <h3>Course Videos</h3>
          <p style={{ margin: 0 }}>Total Videos: 42</p>
        </div>
        <div className="xm-search"><Search size={12} /> Search videos...</div>
        <div className="xm-panel">
          <div className="xm-kv" style={{ background: "transparent", padding: 0 }}>
            <h3>Quadratic equations</h3>
            <ChevronDown size={14} color="#9ca3af" />
          </div>
          <div className="xm-row" style={{ marginTop: 8 }}>
            <div className="xm-ico"><Video size={12} /></div>
            <div className="xm-row-copy">
              <h3>Solving by factorising</h3>
              <p>Worked examples from the lesson.</p>
            </div>
          </div>
          <div className="xm-player"><Play size={16} /> Play lesson</div>
        </div>
      </DashFrame>
    );
  }

  if (id === "markschemes" || id === "extra" || id === "pdfs") {
    const titles = {
      markschemes: ["Mark Schemes", "4MA1 mark scheme — Algebra"],
      extra: ["Extra Questions", "Mixed algebra drill"],
      pdfs: ["PDFs", "Chapter notes — Quadratics"],
    };
    const [heading, file] = titles[id];
    return (
      <DashFrame active={id}>
        <div className="xm-hero-card" style={{ display: "flex", justifyContent: "space-between" }}>
          <h3>{heading}</h3>
          <p style={{ margin: 0 }}>Total Chapters: 8</p>
        </div>
        <div className="xm-panel">
          <div className="xm-row" style={{ padding: 0, border: 0, background: "transparent" }}>
            <div className="xm-ico"><FileText size={12} /></div>
            <div className="xm-row-copy"><h3>Quadratic equations</h3></div>
            <ChevronDown size={14} color="#9ca3af" />
          </div>
          <div className="xm-file">
            <span>{file}</span>
            <Download size={12} color="#9ca3af" />
          </div>
          <div className="xm-file">
            <span>Worked solutions</span>
            <Download size={12} color="#9ca3af" />
          </div>
        </div>
      </DashFrame>
    );
  }

  if (id === "pastpapers") {
    return (
      <DashFrame active="pastpapers">
        <div className="xm-hero-card">
          <h3>Past Papers</h3>
          <p>Access all available past papers for your course</p>
        </div>
        <div className="xm-panel">
          <h3>2024</h3>
          <p className="xm-pill xm-pill-sky" style={{ display: "inline-block", marginTop: 8 }}>June Session</p>
          <div className="xm-row" style={{ marginTop: 8 }}>
            <CheckCircle2 size={14} color="#22c55e" />
            <div className="xm-row-copy"><h3>Paper 1H</h3><p>Calculator</p></div>
          </div>
          <div className="xm-row">
            <Circle size={14} color="#9ca3af" />
            <div className="xm-row-copy"><h3>Paper 2H</h3><p>Calculator</p></div>
          </div>
        </div>
      </DashFrame>
    );
  }

  if (id === "homework") {
    const items = [
      ["Algebra practice set", "Due: Oct 3", "Done", "xm-pill-green"],
      ["Graph sketching", "Due: Oct 8", "Incomplete", "xm-pill-amber"],
      ["Circle theorems", "Due: Oct 12", "Missing", "xm-pill-red"],
    ];
    return (
      <DashFrame active="homework">
        <div className="xm-hero-card" style={{ display: "flex", justifyContent: "space-between" }}>
          <h3>Homework</h3>
          <p style={{ margin: 0 }}>Total Assignments: 12</p>
        </div>
        {items.map(([title, due, status, pill]) => (
          <div className="xm-row" key={title}>
            <div className="xm-ico"><BookText size={12} /></div>
            <div className="xm-row-copy">
              <h3>{title}</h3>
              <p>{due}</p>
            </div>
            <span className={`xm-pill ${pill}`}>{status}</span>
          </div>
        ))}
      </DashFrame>
    );
  }

  if (id === "exams") {
    const rows = [
      ["October mock — Paper 1", "Sep 18", "72/100", "A", "xm-pill-green"],
      ["October mock — Paper 2", "Sep 21", "64/100", "B", "xm-pill-sky"],
      ["September quiz — Algebra", "Sep 4", "31/40", "A", "xm-pill-green"],
    ];
    return (
      <DashFrame active="exams" path="dashboard/exams">
        <div className="xm-hero-card">
          <h3>Exam grades</h3>
          <p>Marks, grades, and teacher comments</p>
        </div>
        <div className="xm-track">
          <div className="xm-track-row xm-track-head xm-track-grades">
            <span>Exam</span>
            <span>Date</span>
            <span>Mark</span>
            <span>Grade</span>
          </div>
          {rows.map(([name, date, mark, grade, pill]) => (
            <div className="xm-track-row xm-track-grades" key={name}>
              <strong>{name}</strong>
              <span>{date}</span>
              <span>{mark}</span>
              <span className={`xm-pill ${pill}`}>{grade}</span>
            </div>
          ))}
        </div>
        <div className="xm-panel">
          <h3>September quiz — Algebra</h3>
          <p className="xm-muted">Comment: Strong factorising. Watch graph scales.</p>
        </div>
      </DashFrame>
    );
  }

  if (id === "meetings") {
    return (
      <DashFrame active="meetings">
        <div className="xm-hero-card" style={{ display: "flex", justifyContent: "space-between" }}>
          <h3>Online Meetings</h3>
          <p style={{ margin: 0 }}>Total Meetings: 6</p>
        </div>
        <div className="xm-search"><Search size={12} /> Search meetings...</div>
        <div className="xm-panel">
          <div className="xm-row" style={{ padding: 0, border: 0, background: "transparent" }}>
            <div className="xm-ico"><CalendarDays size={12} /></div>
            <div className="xm-row-copy">
              <h3>Thursday live class</h3>
              <p>Oct 2, 5:00 PM · Upcoming</p>
              <p>Circle theorems and exam technique.</p>
            </div>
          </div>
          <span className="xm-pill xm-pill-green" style={{ display: "inline-block", marginTop: 8 }}>Join Meeting</span>
        </div>
      </DashFrame>
    );
  }

  if (id === "announcements") {
    return (
      <DashFrame active="announcements">
        <h3 style={{ fontSize: "1rem", margin: "0.2rem 0 0.2rem" }}>Announcements</h3>
        <div className="xm-panel">
          <div className="xm-kv" style={{ background: "transparent", padding: 0 }}>
            <h3>Mock week timetable</h3>
            <span className="xm-pill xm-pill-blue">Sep 28</span>
          </div>
          <p className="xm-muted">Paper 2 mock is on Thursday. Bring a calculator and tracing paper.</p>
        </div>
        <div className="xm-panel">
          <div className="xm-kv" style={{ background: "transparent", padding: 0 }}>
            <h3>New past papers uploaded</h3>
            <span className="xm-pill xm-pill-blue">Sep 20</span>
          </div>
          <p className="xm-muted">June 2024 papers are now in Past Papers.</p>
        </div>
      </DashFrame>
    );
  }

  return (
    <DashFrame active="profile">
      <div className="xm-hero-card xm-profile-head">
        <div className="xm-avatar">LH</div>
        <div>
          <h3>Lina Hassan</h3>
          <p>IGCSE OL Mathematics</p>
        </div>
      </div>
      <div className="xm-split">
        <div className="xm-panel">
          <h4>Personal Information</h4>
          <div className="xm-kv"><span>Full Name</span><strong>Lina Hassan</strong></div>
          <div className="xm-kv"><span>Email</span><strong>lina@email.com</strong></div>
          <div className="xm-kv"><span>Student ID</span><strong>XM-2041</strong></div>
        </div>
        <div className="xm-panel">
          <h4>Academic Information</h4>
          <div className="xm-kv"><span>Current Level</span><strong>IGCSE OL</strong></div>
          <div className="xm-kv"><span>Status</span><strong className="xm-pill xm-pill-green">Active</strong></div>
        </div>
      </div>
    </DashFrame>
  );
}

const StudentDashboardTour = () => {
  const [openId, setOpenId] = useState(null);
  const openFeature = FEATURES.find((feature) => feature.id === openId);

  useEffect(() => {
    if (!openId) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpenId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openId]);

  return (
    <div className="xm-page">
      <Navbar />

      <section className="xm-about-hero">
        <div className="xm-wrap">
          <p className="xm-kicker">Inside the class</p>
          <h1 className="xm-section-title xm-display" style={{ maxWidth: "16ch" }}>
            See the class the way students do.
          </h1>
          <p className="xm-section-copy" style={{ marginTop: "1rem" }}>
            Lessons, files, homework, exam grades, live classes, and announcements —
            shown with sample data so you can look around before you enrol.
          </p>
        </div>
      </section>

      <section className="xm-section">
        <div className="xm-wrap xm-glance-list">
          {FEATURES.map((feature) => (
            <article className="xm-glance-item" id={`glance-${feature.id}`} key={feature.id}>
              <div className="xm-glance-copy">
                <p className="xm-kicker">{feature.label}</p>
                <h2>{feature.title}</h2>
                <p>{feature.copy}</p>
              </div>
              <div>
                <button
                  type="button"
                  className="xm-shot-btn"
                  onClick={() => setOpenId(feature.id)}
                  aria-label={`Enlarge ${feature.label} preview`}
                >
                  <Screen id={feature.id} />
                </button>
                <p className="xm-shot-note">Sample preview.</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="xm-cta-band">
        <div className="xm-wrap">
          <h2 className="xm-section-title xm-display">Ready to step inside?</h2>
          <p className="xm-section-copy">
            Create an account, finish registration, and these screens open for your course.
          </p>
          <div className="xm-cta-row" style={{ justifyContent: "center" }}>
            <Link to="/signup" className="xm-btn xm-btn-primary">
              Sign Up <FaArrowRight />
            </Link>
            <Link to="/login" className="xm-btn xm-btn-ghost">
              Student login
            </Link>
          </div>
        </div>
      </section>

      {openFeature && (
        <div className="xm-lightbox" role="dialog" aria-modal="true" aria-label={`${openFeature.label} preview`} onClick={() => setOpenId(null)}>
          <div className="xm-lightbox-card" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="xm-lightbox-close" onClick={() => setOpenId(null)}>
              Close
            </button>
            <Screen id={openFeature.id} />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default StudentDashboardTour;
